'use client';

import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  EstimatorConfig, 
  EstimatorSelection, 
  EstimatorStep,
  ContactInfo 
} from './types';
import { calculateOptimizedPrice, formatTimeline, formatPrice, generateCostAnalysisReport } from './utils/optimizedPriceCalculator';
import styles from './UnifiedEstimator.module.scss';

// Register GSAP plugins
if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger);
}

interface OptimizedEstimatorProps {
  config: EstimatorConfig;
  onComplete?: (selection: EstimatorSelection, contact: ContactInfo) => void;
  className?: string;
}

export function OptimizedEstimator({ 
  config, 
  onComplete: _onComplete,
  className = '' 
}: OptimizedEstimatorProps) {
  // SSR compatibility - ensure component is mounted
  const [isMounted, setIsMounted] = useState(false);
  
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [selection, setSelection] = useState<EstimatorSelection>({
    packageId: config.packages[0]?.id || '',
    optionIds: [],
    quantity: 1,
    urgency: 'normal'
  });
  const [_contactInfo, _setContactInfo] = useState<ContactInfo>({
    name: '',
    email: '',
    phone: '',
    companyName: '',
    message: '',
    preferredContact: 'email'
  });
  const [_errors, _setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // GSAP refs
  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const packagesRef = useRef<HTMLDivElement>(null);
  const priceDisplayRef = useRef<HTMLDivElement>(null);

  // Define steps
  const steps: EstimatorStep[] = [
    {
      id: 'package',
      title: 'サービス選択',
      description: '最適なプランをお選びください',
      completed: false,
      current: true
    },
    {
      id: 'options',
      title: 'オプション選択',
      description: '必要な追加機能をお選びください',
      completed: false,
      current: false
    },
    {
      id: 'details',
      title: '詳細設定',
      description: '数量や納期をご指定ください',
      completed: false,
      current: false
    },
    {
      id: 'summary',
      title: '最適化結果',
      description: '外注費・工数分析に基づく最適価格',
      completed: false,
      current: false
    }
  ];

  // Update steps based on current step
  const updatedSteps = steps.map((step, index) => ({
    ...step,
    completed: index < currentStep,
    current: index === currentStep
  }));

  // Calculate optimized price
  const priceBreakdown = useMemo(() => 
    calculateOptimizedPrice(config, selection),
    [config, selection]
  );

  // Get selected package
  const selectedPackage = useMemo(() => 
    config.packages.find(p => p.id === selection.packageId),
    [config.packages, selection.packageId]
  );

  // Get available options for selected package
  const availableOptions = useMemo(() => 
    config.options.filter(option => 
      !option.requiresPackage || 
      option.requiresPackage.includes(selection.packageId)
    ),
    [config.options, selection.packageId]
  );

  // GSAP animations (disabled to prevent blank screen issues)
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    // Initial entrance animation - disabled for stability
    // const tl = gsap.timeline();
    
    // tl.from(headerRef.current, {
    //   y: -50,
    //   opacity: 0,
    //   duration: 0.8,
    //   ease: 'power3.out'
    // });
    
    // if (progressRef.current) {
    //   tl.from(progressRef.current.querySelectorAll(`.${styles.progressStep}`), {
    //     x: -30,
    //     opacity: 0,
    //     duration: 0.6,
    //     stagger: 0.1,
    //     ease: 'power2.out'
    //   }, '-=0.4');
    // }
    
    // if (contentRef.current) {
    //   tl.from(contentRef.current, {
    //   y: 30,
    //   opacity: 0,
    //   duration: 0.6,
    //   ease: 'power2.out'
    // }, '-=0.3');
    // }

    // ScrollTrigger for progressive reveal - disabled for stability
    // if (packagesRef.current) {
    //   gsap.from(packagesRef.current.children, {
    //     scrollTrigger: {
    //       trigger: packagesRef.current,
    //       start: 'top 80%',
    //       end: 'bottom 20%',
    //       toggleActions: 'play none none reverse'
    //     },
    //     y: 50,
    //     opacity: 0,
    //     duration: 0.8,
    //     stagger: 0.2,
    //     ease: 'power2.out'
    //   });
    // }

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Animate step transitions - disabled for stability
  useEffect(() => {
    if (!contentRef.current) {
      return;
    }

    // gsap.fromTo(contentRef.current,
    //   { 
    //     x: currentStep > 0 ? 100 : -100,
    //     opacity: 0 
    //   },
    //   {
    //     x: 0,
    //     opacity: 1,
    //     duration: 0.6,
    //     ease: 'power2.out'
    //   }
    // );

    // Animate progress steps - disabled for stability
    // progressRef.current?.querySelectorAll(`.${styles.progressStep}`).forEach((step, index) => {
    //   if (index <= currentStep) {
    //     gsap.to(step, {
    //       scale: index === currentStep ? 1.1 : 1,
    //       duration: 0.3,
    //       ease: 'back.out(1.7)'
    //     });
    //   }
    // });
  }, [currentStep]);

  // Animate price changes - disabled for stability
  useEffect(() => {
    if (!priceDisplayRef.current) {
      return;
    }

    // gsap.fromTo(priceDisplayRef.current,
    //   { scale: 0.9, opacity: 0.5 },
    //   { 
    //     scale: 1, 
    //     opacity: 1, 
    //     duration: 0.5,
    //     ease: 'elastic.out(1, 0.3)'
    //   }
    // );
  }, [priceBreakdown.total]);

  // Handlers
  const handlePackageSelect = useCallback((packageId: string) => {
    setSelection(prev => ({
      ...prev,
      packageId,
      optionIds: prev.optionIds.filter(id => {
        const option = config.options.find(o => o.id === id);
        return !option?.requiresPackage || option.requiresPackage.includes(packageId);
      })
    }));

    // Animate package selection
    const selectedElement = document.querySelector(`[data-package-id="${packageId}"]`);
    if (selectedElement) {
      gsap.to(selectedElement, {
        scale: 1.05,
        duration: 0.3,
        ease: 'back.out(1.7)',
        onComplete: () => {
          gsap.to(selectedElement, {
            scale: 1,
            duration: 0.2
          });
        }
      });
    }
  }, [config.options]);

  const handleOptionToggle = useCallback((optionId: string) => {
    setSelection(prev => ({
      ...prev,
      optionIds: prev.optionIds.includes(optionId)
        ? prev.optionIds.filter(id => id !== optionId)
        : [...prev.optionIds, optionId]
    }));

    // Animate option toggle
    const optionElement = document.querySelector(`[data-option-id="${optionId}"]`);
    if (optionElement) {
      gsap.to(optionElement, {
        x: 10,
        duration: 0.2,
        ease: 'power2.out',
        yoyo: true,
        repeat: 1
      });
    }
  }, []);

  const handleNextStep = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    }
  }, [currentStep, steps.length]);

  const handlePrevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    // For simulation mode, just redirect to the contact page
    setIsSubmitting(true);
    
    // Animate submit button
    const submitButton = document.querySelector(`.${styles.submitButton}`);
    if (submitButton) {
      gsap.to(submitButton, {
        scale: 0.95,
        duration: 0.1,
        yoyo: true,
        repeat: 1
      });
    }
    
    try {
      // Store optimized simulation data in session storage
      const costAnalysisReport = generateCostAnalysisReport(config, selection);
      sessionStorage.setItem('optimizedEstimatorSelection', JSON.stringify({
        service: config.title,
        package: selectedPackage?.name,
        options: selection.optionIds.length,
        price: priceBreakdown.total,
        timeline: priceBreakdown.timeline ? priceBreakdown.timeline.total : null,
        urgency: selection.urgency,
        costAnalysis: priceBreakdown.costAnalysis,
        costReduction: priceBreakdown.costReduction,
        costReductionPercentage: priceBreakdown.costReductionPercentage,
        traditionalPrice: priceBreakdown.traditionalPrice,
        report: costAnalysisReport
      }));
      
      // Redirect to quote page
      window.location.href = '/contact/sales/quote';
    } finally {
      setIsSubmitting(false);
    }
  }, [config, selectedPackage?.name, selection, priceBreakdown]);

  // Animation variants (simplified to avoid blank screen issues)
  const containerVariants = {
    hidden: { opacity: 1 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 1, y: 0 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  const stepVariants = {
    enter: { x: 0, opacity: 1 },
    center: { x: 0, opacity: 1 },
    exit: { x: 0, opacity: 1 }
  };

  // Simplified fallback for debugging
  if (!isMounted || !config || !config.packages || config.packages.length === 0) {
    return (
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '2rem', 
        backgroundColor: '#f8f9fa', 
        borderRadius: '8px',
        border: '1px solid #dee2e6'
      }}>
        <div style={{ marginBottom: '1rem' }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#333' }}>
            {config?.title || '見積もりシミュレーター'}
          </h2>
          <p style={{ color: '#666', marginBottom: '1rem' }}>
            {config?.description || '見積もりシミュレーターを読み込んでいます...'}
          </p>
        </div>
        
        {!isMounted && (
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#fff3cd', 
            border: '1px solid #ffeaa7',
            borderRadius: '4px',
            color: '#856404'
          }}>
            <p>コンポーネントを初期化中です...</p>
          </div>
        )}
        
        {isMounted && (!config || !config.packages || config.packages.length === 0) && (
          <div style={{ 
            padding: '1rem', 
            backgroundColor: '#f8d7da', 
            border: '1px solid #f5c6cb',
            borderRadius: '4px',
            color: '#721c24'
          }}>
            <p>設定データの読み込みに失敗しました。</p>
            <p>Config: {config ? 'あり' : 'なし'}</p>
            <p>Packages: {config?.packages?.length || 0}個</p>
          </div>
        )}
      </div>
    );
  }

  // Error boundary protection
  try {
    return (
      <motion.div 
        ref={containerRef}
        className={`${styles.estimator} ${className}`}
        variants={containerVariants}
      >
      {/* Header */}
      <motion.div ref={headerRef} className={styles.header} variants={itemVariants}>
        <h2 className={styles.title}>{config.title}</h2>
        <p className={styles.description}>{config.description}</p>
        <div className={styles.optimizationBadge}>
          <span>外注費・工数分析による最適価格</span>
        </div>
      </motion.div>

      {/* Progress Steps */}
      <motion.div ref={progressRef} className={styles.progress} variants={itemVariants}>
        {updatedSteps.map((step, index) => (
          <div 
            key={step.id} 
            className={`${styles.progressStep} ${step.completed ? styles.completed : ''} ${step.current ? styles.current : ''}`}
          >
            <div className={styles.stepIndicator}>
              <span className={styles.stepNumber}>{index + 1}</span>
            </div>
            <div className={styles.stepInfo}>
              <h4 className={styles.stepTitle}>{step.title}</h4>
              <p className={styles.stepDescription}>{step.description}</p>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Content Area */}
      <AnimatePresence mode="wait">
        <motion.div
          ref={contentRef}
          key={currentStep}
          className={styles.content}
          variants={stepVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.3 }}
        >
          {/* Step 1: Package Selection */}
          {currentStep === 0 && (
            <div className={styles.packageSelection}>
              <div ref={packagesRef} className={styles.packages}>
                {config.packages.map(pkg => (
                  <motion.div
                    key={pkg.id}
                    data-package-id={pkg.id}
                    className={`${styles.package} ${selection.packageId === pkg.id ? styles.selected : ''} ${pkg.popular ? styles.popular : ''}`}
                    onClick={() => handlePackageSelect(pkg.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {pkg.popular && <span className={styles.popularBadge}>おすすめ</span>}
                    <h3 className={styles.packageName}>{pkg.name}</h3>
                    <p className={styles.packageDescription}>{pkg.description}</p>
                    <div className={styles.packagePrice}>
                      <span className={styles.priceAmount}>{formatPrice(pkg.price)}</span>
                      <span className={styles.priceUnit}>〜</span>
                    </div>
                    {pkg.savings && (
                      <p className={styles.packageSavings}>{pkg.savings}</p>
                    )}
                    {pkg.bestFor && (
                      <p className={styles.packageBestFor}>こんな方におすすめ: {pkg.bestFor}</p>
                    )}
                    <ul className={styles.packageFeatures}>
                      {pkg.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Options Selection */}
          {currentStep === 1 && (
            <div className={styles.optionsSelection}>
              <div className={styles.optionCategories}>
                {['essential', 'enhancement', 'support'].map(category => {
                  const categoryOptions = availableOptions.filter(opt => opt.category === category);
                  if (categoryOptions.length === 0) {
                    return null;
                  }

                  return (
                    <div key={category} className={styles.optionCategory}>
                      <h3 className={styles.categoryTitle}>
                        {category === 'essential' && '必須オプション'}
                        {category === 'enhancement' && '機能拡張'}
                        {category === 'support' && 'サポート'}
                      </h3>
                      <div className={styles.options}>
                        {categoryOptions.map(option => (
                          <motion.div
                            key={option.id}
                            data-option-id={option.id}
                            className={`${styles.option} ${selection.optionIds.includes(option.id) ? styles.selected : ''}`}
                            onClick={() => handleOptionToggle(option.id)}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            <div className={styles.optionHeader}>
                              <div className={styles.optionCheck}>
                                <input
                                  type="checkbox"
                                  checked={selection.optionIds.includes(option.id)}
                                  onChange={() => {}}
                                />
                              </div>
                              <div className={styles.optionInfo}>
                                <h4 className={styles.optionName}>{option.name}</h4>
                                <p className={styles.optionDescription}>{option.description}</p>
                              </div>
                              <div className={styles.optionPrice}>
                                +{formatPrice(option.price)}
                              </div>
                            </div>
                            {option.popular && (
                              <span className={styles.popularTag}>人気</span>
                            )}
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Step 3: Details */}
          {currentStep === 2 && (
            <div className={styles.detailsStep}>
              <div className={styles.detailsForm}>
                <div className={styles.formGroup}>
                  <label htmlFor="quantity">数量</label>
                  <input
                    id="quantity"
                    type="number"
                    min="1"
                    value={selection.quantity}
                    onChange={(e) => setSelection(prev => ({ ...prev, quantity: parseInt(e.target.value) || 1 }))}
                    className={styles.quantityInput}
                  />
                </div>

                <div className={styles.formGroup}>
                  <p className={styles.fieldLabel}>納期の希望</p>
                  <div className={styles.urgencyOptions}>
                    {[
                      { value: 'flexible', label: 'ゆとりあり', description: '通常納期より長め（5%割引）', timeline: priceBreakdown.timeline ? formatTimeline(priceBreakdown.timeline.total * 1.2, priceBreakdown.timeline.unit) : '' },
                      { value: 'normal', label: '標準', description: '標準的な納期', timeline: priceBreakdown.timeline ? formatTimeline(priceBreakdown.timeline.total, priceBreakdown.timeline.unit) : '' },
                      { value: 'urgent', label: 'お急ぎ', description: '優先対応（50%追加料金）', timeline: priceBreakdown.timeline ? formatTimeline(priceBreakdown.timeline.total * 0.7, priceBreakdown.timeline.unit) : '' }
                    ].map(urgency => (
                      <motion.div 
                        key={urgency.value} 
                        className={`${styles.urgencyOption} ${selection.urgency === urgency.value ? styles.selected : ''}`}
                        onClick={() => setSelection(prev => ({ ...prev, urgency: urgency.value as 'normal' | 'urgent' | 'flexible' }))}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <input
                          id={`urgency-${urgency.value}`}
                          type="radio"
                          name="urgency"
                          value={urgency.value}
                          checked={selection.urgency === urgency.value}
                          onChange={() => {}}
                          className={styles.hiddenRadio}
                        />
                        <div className={styles.urgencyContent}>
                          <div className={styles.urgencyHeader}>
                            <span className={styles.urgencyLabel}>{urgency.label}</span>
                            <span className={styles.urgencyTimeline}>{urgency.timeline}</span>
                          </div>
                          <span className={styles.urgencyDescription}>{urgency.description}</span>
                        </div>
                        <div className={styles.radioIndicator}>
                          <div className={styles.radioButton}>
                            {selection.urgency === urgency.value && <div className={styles.radioChecked} />}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label htmlFor="notes">備考・ご要望</label>
                  <textarea
                    id="notes"
                    value={selection.notes || ''}
                    onChange={(e) => setSelection(prev => ({ ...prev, notes: e.target.value }))}
                    className={styles.notesInput}
                    rows={4}
                    placeholder="ご要望や特記事項がございましたらご記入ください"
                  />
                </div>
              </div>

              {/* Optimized Price Summary */}
              <div className={styles.priceSummary}>
                <h3 className={styles.summaryTitle}>最適化価格内訳</h3>
                <div className={styles.priceBreakdown}>
                  <div className={styles.priceRow}>
                    <span>基本料金（{selectedPackage?.name}）</span>
                    <span>{formatPrice(priceBreakdown.packagePrice)}</span>
                  </div>
                  {priceBreakdown.optionsPrice > 0 && (
                    <div className={styles.priceRow}>
                      <span>オプション料金</span>
                      <span>{formatPrice(priceBreakdown.optionsPrice)}</span>
                    </div>
                  )}
                  <div className={styles.priceRow}>
                    <span>小計</span>
                    <span>{formatPrice(priceBreakdown.subtotal)}</span>
                  </div>
                  <div className={styles.priceRow}>
                    <span>消費税（10%）</span>
                    <span>{formatPrice(priceBreakdown.tax)}</span>
                  </div>
                  <div className={`${styles.priceRow} ${styles.total}`}>
                    <span>最適化価格</span>
                    <span ref={priceDisplayRef}>{formatPrice(priceBreakdown.total)}</span>
                  </div>
                </div>

                {/* Cost Analysis Summary */}
                <div className={styles.costAnalysis}>
                  <h4 className={styles.analysisTitle}>コスト分析</h4>
                  <div className={styles.analysisBreakdown}>
                    <div className={styles.analysisRow}>
                      <span>作業期間</span>
                      <span>{priceBreakdown.costAnalysis.workDays}日</span>
                    </div>
                    <div className={styles.analysisRow}>
                      <span>固定費</span>
                      <span>{formatPrice(priceBreakdown.costAnalysis.fixedCost)}</span>
                    </div>
                    <div className={styles.analysisRow}>
                      <span>外注費</span>
                      <span>{formatPrice(priceBreakdown.costAnalysis.outsourcingCost)}</span>
                    </div>
                    <div className={styles.analysisRow}>
                      <span>従来価格</span>
                      <span>{formatPrice(priceBreakdown.traditionalPrice)}</span>
                    </div>
                    <div className={`${styles.analysisRow} ${styles.savings}`}>
                      <span>コスト削減</span>
                      <span>{formatPrice(priceBreakdown.costReduction)} ({priceBreakdown.costReductionPercentage}%)</span>
                    </div>
                  </div>
                </div>

                {/* Timeline Summary */}
                {priceBreakdown.timeline && (
                  <div className={styles.timelineSummary}>
                    <h4 className={styles.timelineTitle}>予想制作期間</h4>
                    <div className={styles.timelineBreakdown}>
                      <div className={styles.timelineRow}>
                        <span>開発・制作</span>
                        <span>{formatTimeline(priceBreakdown.timeline.development, priceBreakdown.timeline.unit)}</span>
                      </div>
                      <div className={styles.timelineRow}>
                        <span>検証・調整</span>
                        <span>{formatTimeline(priceBreakdown.timeline.testing, priceBreakdown.timeline.unit)}</span>
                      </div>
                      <div className={styles.timelineRow}>
                        <span>納品・導入</span>
                        <span>{formatTimeline(priceBreakdown.timeline.deployment, priceBreakdown.timeline.unit)}</span>
                      </div>
                      <div className={`${styles.timelineRow} ${styles.totalTimeline}`}>
                        <span>合計期間</span>
                        <span>{formatTimeline(priceBreakdown.timeline.total, priceBreakdown.timeline.unit)}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Step 4: Optimized Summary */}
          {currentStep === 3 && (
            <div className={styles.simulationSummary}>
              <div className={styles.simulationHeader}>
                <h3 className={styles.simulationTitle}>最適化価格シミュレーション完了</h3>
                <p className={styles.simulationDescription}>
                  外注費・工数分析による最適価格を算出しました。正式なお見積もりは下記よりお進みください。
                </p>
              </div>

              <div className={styles.summaryGrid}>
                {/* Optimized Price Display */}
                <div className={styles.finalPriceSummary}>
                  <h4 className={styles.priceTitle}>最適化価格</h4>
                  <div className={styles.finalPriceAmount}>
                    {formatPrice(priceBreakdown.total)}
                    <span className={styles.taxIncluded}>（税込）</span>
                  </div>
                  <div className={styles.priceNote}>
                    従来価格 {formatPrice(priceBreakdown.traditionalPrice)} から<br />
                    <strong>{formatPrice(priceBreakdown.costReduction)} ({priceBreakdown.costReductionPercentage}%) コストダウン</strong>
                  </div>
                </div>

                {/* Timeline Display */}
                {priceBreakdown.timeline && (
                  <div className={styles.finalTimelineSummary}>
                    <h4 className={styles.timelineTitle}>実制作期間</h4>
                    <div className={styles.finalTimelineAmount}>
                      {formatTimeline(priceBreakdown.timeline.total, priceBreakdown.timeline.unit)}
                    </div>
                    <div className={styles.timelineNote}>
                      作業期間: {priceBreakdown.costAnalysis.workDays}日<br />
                      {priceBreakdown.costAnalysis.description}
                    </div>
                  </div>
                )}

                {/* Cost Analysis Summary */}
                <div className={styles.selectedSummary}>
                  <h4 className={styles.selectionTitle}>コスト分析結果</h4>
                  <div className={styles.selectionDetails}>
                    <p><strong>固定費:</strong> {formatPrice(priceBreakdown.costAnalysis.fixedCost)}</p>
                    <p><strong>外注費:</strong> {formatPrice(priceBreakdown.costAnalysis.outsourcingCost)}</p>
                    <p><strong>利益:</strong> {formatPrice(priceBreakdown.costAnalysis.profitMargin)}</p>
                    <p><strong>プラン:</strong> {selectedPackage?.name}</p>
                    {selection.optionIds.length > 0 && (
                      <p><strong>オプション:</strong> {selection.optionIds.length}件</p>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.nextSteps}>
                <h4 className={styles.nextStepsTitle}>次のステップ</h4>
                <div className={styles.nextStepsContent}>
                  <div className={styles.nextStepItem}>
                    <span className={styles.stepNumber}>1</span>
                    <div className={styles.stepInfo}>
                      <h5>詳細ヒアリング</h5>
                      <p>最適化結果をもとに、具体的な要件をお伺いします</p>
                    </div>
                  </div>
                  <div className={styles.nextStepItem}>
                    <span className={styles.stepNumber}>2</span>
                    <div className={styles.stepInfo}>
                      <h5>正式見積もり</h5>
                      <p>外注費・工数分析を反映した正確な見積もりを提示</p>
                    </div>
                  </div>
                  <div className={styles.nextStepItem}>
                    <span className={styles.stepNumber}>3</span>
                    <div className={styles.stepInfo}>
                      <h5>効率的な制作開始</h5>
                      <p>最適化された工程で高品質なサービスを提供</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.ctaSection}>
                <p className={styles.ctaMessage}>
                  この最適化価格をもとに、正式なお見積もりをご提案いたします
                </p>
                <motion.button
                  className={styles.ctaButton}
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  {isSubmitting ? '処理中...' : '最適化価格での見積もり依頼'}
                </motion.button>
                <p className={styles.ctaSubtext}>
                  ※外注費・工数分析結果を含む詳細資料をお渡しします
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation */}
      <motion.div className={styles.navigation} variants={itemVariants}>
        <button
          className={styles.prevButton}
          onClick={handlePrevStep}
          disabled={currentStep === 0}
        >
          前へ戻る
        </button>
        
        {currentStep < steps.length - 1 ? (
          <button
            className={styles.nextButton}
            onClick={handleNextStep}
          >
            次へ進む
          </button>
        ) : (
          <button
            className={`${styles.submitButton} ${isSubmitting ? styles.submitting : ''}`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? '処理中...' : '最適化完了'}
          </button>
        )}
      </motion.div>
    </motion.div>
  );
  } catch (error) {
    console.error('OptimizedEstimator rendering error:', error);
    return (
      <div style={{ 
        maxWidth: '800px', 
        margin: '0 auto', 
        padding: '2rem', 
        backgroundColor: '#f8d7da', 
        borderRadius: '8px',
        border: '1px solid #f5c6cb'
      }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', color: '#721c24' }}>
          見積もりシミュレーターエラー
        </h2>
        <p style={{ color: '#721c24', marginBottom: '1rem' }}>
          コンポーネントの表示中にエラーが発生しました。
        </p>
        <details style={{ color: '#721c24', fontSize: '0.9rem' }}>
          <summary>エラー詳細</summary>
          <pre style={{ backgroundColor: '#f5c6cb', padding: '0.5rem', borderRadius: '4px', marginTop: '0.5rem' }}>
            {error instanceof Error ? error.message : String(error)}
          </pre>
        </details>
      </div>
    );
  }
}

export default OptimizedEstimator;